import { useState } from 'react';
import { ArrowRight, Calculator, MapPin, PackageCheck, Phone } from 'lucide-react';
import { quoteService } from '../services/quoteService.js';

const initialForm = {
  distanceMiles: '',
  packageWeight: '',
  serviceType: 'Same day delivery',
  urgency: 'standard',
};

const serviceOptions = [
  ['Same day delivery', 'Same day delivery'],
  ['Scheduled delivery', 'Scheduled delivery'],
  ['Final mile delivery', 'Final mile delivery'],
  ['Medical transportation', 'Medical courier'],
  ['Hot shot delivery', 'Hot shot delivery'],
  ['Long haul', 'Long distance delivery'],
  ['Warehouse distribution', 'Warehouse distribution'],
];

export function QuoteCalculator() {
  const [form, setForm] = useState(initialForm);
  const [estimate, setEstimate] = useState(null);
  const [customQuote, setCustomQuote] = useState(false);
  const [loading, setLoading] = useState(false);

  const update = (event) => setForm((current) => ({ ...current, [event.target.name]: event.target.value }));

  async function calculate(event) {
    event.preventDefault();
    setCustomQuote(false);
    setEstimate(null);
    setLoading(true);
    try {
      const result = await quoteService.requestEstimate({
        distanceMiles: Number(form.distanceMiles),
        packageWeight: Number(form.packageWeight),
        serviceType: form.serviceType,
        urgency: form.urgency,
      });
      setEstimate(result.estimate);
    } catch {
      setCustomQuote(true);
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="estimate-section" id="estimate" aria-labelledby="estimate-title">
      <div className="estimate-intro">
        <p className="eyebrow">Plan your delivery</p>
        <h2 id="estimate-title">Estimate by distance and weight.</h2>
        <p>Enter the expected one way route mileage and total shipment weight. The estimate is calculated by our protected server pricing rules.</p>
        <div className="estimate-features">
          <span><MapPin size={18} /> Route mileage</span>
          <span><PackageCheck size={18} /> Shipment weight</span>
          <span><Calculator size={18} /> Server calculated pricing</span>
        </div>
      </div>
      <div className="estimate-card">
        <form onSubmit={calculate}>
          <label>One way distance in miles <span aria-hidden="true">*</span><input name="distanceMiles" type="number" min="0.1" max="5000" step="0.1" value={form.distanceMiles} onChange={update} required /></label>
          <label>Total shipment weight in pounds <span aria-hidden="true">*</span><input name="packageWeight" type="number" min="0" max="100000" step="0.1" value={form.packageWeight} onChange={update} required /></label>
          <label>Service type <span aria-hidden="true">*</span><select name="serviceType" value={form.serviceType} onChange={update}>{serviceOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <label>Delivery priority <span aria-hidden="true">*</span><select name="urgency" value={form.urgency} onChange={update}><option value="standard">Standard</option><option value="expedited">Expedited</option><option value="urgent">Urgent</option></select></label>
          <button className="button button-primary" type="submit" disabled={loading}>{loading ? 'Calculating…' : 'Calculate estimate'} <ArrowRight size={16} /></button>
        </form>
        {customQuote && <div className="estimate-result estimate-custom" role="status">
          <p>Custom quote required</p>
          <strong>Let our dispatch team review the route.</strong>
          <span>Pricing depends on mileage, timing, shipment size, handling requirements, vehicle type, and current availability.</span>
          <div className="estimate-result-actions"><a href="/request-delivery">Complete delivery request <ArrowRight size={15} /></a><a href="tel:+18664245878"><Phone size={15}/> Call 866 4JUSTGO</a></div>
        </div>}
        {estimate && <div className="estimate-result" aria-live="polite">
          <p>Estimated delivery price</p>
          <strong>{new Intl.NumberFormat('en-US', { style: 'currency', currency: estimate.currency || 'USD' }).format(estimate.total)}</strong>
          <span>This estimate is not a final quote. Tolls, waiting time, access conditions, vehicle requirements, handling needs, and inaccurate mileage or weight may change the confirmed price.</span>
          <a href="#quote">Continue with delivery request <ArrowRight size={15} /></a>
        </div>}
        <p className="estimate-disclaimer">Use accurate road mileage, not straight line distance. Service and final pricing remain subject to review, availability, and confirmation.</p>
      </div>
    </section>
  );
}
